/**
 * @file Reusable sortable table — and, on phones, the same rows as a card
 * list. Drop-in replacement for the hand-built `<table class="list">`
 * blocks across the app so column-header sorting works the same everywhere
 * (previously People had ad-hoc partial sorting and most tables had none).
 *
 * Usage:
 *   const table = sortableTable({
 *     columns: [
 *       { label: 'Name',  sortValue: (r) => r.fullName },          // sortable
 *       { label: 'Email', sortValue: (r) => r.email, align: 'left' },
 *       { label: '',      sortable: false },                       // actions col
 *     ],
 *     rows,
 *     renderRow:  (r) => el('tr', { onClick: … }, el('td', …), …),
 *     renderCard: (r) => listCard({ title: r.fullName, meta: [r.email], … }), // optional
 *     initialSort: { index: 0, dir: 'asc' },
 *     onOrder: ({ rows, sortIndex, sortDir }) => …,  // optional: fires after every paint
 *   });
 *   host.appendChild(table);
 *
 * Sorting is in-memory and stable. `sortValue` returns the comparable key
 * for a row (string / number / Date / Firestore Timestamp / null). Null and
 * undefined always sort last regardless of direction. Columns without a
 * `sortValue` (or `sortable: false`) render as plain, non-clickable headers.
 *
 * The number/order of `columns` MUST match the cells `renderRow` emits.
 *
 * Phones: with `renderCard`, viewports ≤600px get a `.card-list` instead of
 * the table — one `listCard` per row and a single sort <select> built from
 * the sortable columns ("Name ▲", "Name ▼", …). A matchMedia listener flips
 * modes live when the viewport crosses the breakpoint. Without `renderCard`
 * the table always renders. Either way the result sits in a `.table-scroll`
 * wrapper, so a wide table pans sideways instead of being clipped by the
 * page's overflow-x:hidden.
 */

import { el } from '../utils.js';

const MOBILE = typeof matchMedia === 'function' ? matchMedia('(max-width: 600px)') : null;

export function sortableTable({ columns, rows, renderRow, renderCard = null, initialSort = null, onOrder = null, tableClass = 'list' }) {
  let sortIndex = initialSort?.index ?? -1;
  let sortDir   = initialSort?.dir   ?? 'asc';
  const isSortable = (col) => col.sortable !== false && typeof col.sortValue === 'function';

  function sortedRows() {
    if (sortIndex < 0 || !columns[sortIndex]?.sortValue) return rows;
    const getVal = columns[sortIndex].sortValue;
    const sign = sortDir === 'desc' ? -1 : 1;
    // Stable sort: decorate with original index, compare, then strip.
    return rows
      .map((r, i) => [r, i])
      .sort((a, b) => {
        const c = cmp(getVal(a[0]), getVal(b[0]));
        return c !== 0 ? c * sign : a[1] - b[1];
      })
      .map((pair) => pair[0]);
  }

  // ── Table mode ──────────────────────────────────────────────────────────
  let tbody = null;
  let headCells = [];
  function buildTable() {
    tbody = el('tbody');
    headCells = columns.map((col, i) => {
      const sortable = isSortable(col);
      const indicator = el('span', { class: 'sort-indicator', 'aria-hidden': 'true' }, '');
      const th = el('th', {
        class: sortable ? 'sortable' : null,
        style: col.thStyle || (col.align === 'right' ? 'text-align:right' : null),
        role: sortable ? 'button' : null,
        tabindex: sortable ? '0' : null,
        title: sortable ? `Sort by ${col.label || 'column'}` : null,
      }, col.label || '', sortable ? indicator : null);
      if (sortable) {
        const activate = () => {
          if (sortIndex === i) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
          else { sortIndex = i; sortDir = 'asc'; }
          paintTable();
        };
        th.addEventListener('click', activate);
        th.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); }
        });
      }
      return { th, indicator, sortable };
    });
    return el('table', { class: tableClass },
      el('thead', null, el('tr', null, ...headCells.map((hc) => hc.th))),
      tbody,
    );
  }
  function paintTable() {
    headCells.forEach((hc, i) => {
      if (!hc.sortable) return;
      hc.th.classList.toggle('sorted', i === sortIndex);
      hc.indicator.textContent = i === sortIndex ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
    });
    const sorted = sortedRows();
    tbody.replaceChildren(...sorted.map(renderRow));
    onOrder?.({ rows: sorted, sortIndex, sortDir });
  }

  // ── Card mode (phones) ─────────────────────────────────────────────────
  let list = null;
  let sortSelect = null;
  function buildCards() {
    const options = [];
    columns.forEach((col, i) => {
      if (!isSortable(col)) return;
      options.push(el('option', { value: `${i}:asc` },  `${col.label} ▲`));
      options.push(el('option', { value: `${i}:desc` }, `${col.label} ▼`));
    });
    // No explicit initial sort → sort by the first sortable column so the
    // select never lies about the order on screen.
    if (sortIndex < 0 && options.length) [sortIndex, sortDir] = options[0].value.split(':');
    sortSelect = options.length ? el('select', { 'aria-label': 'Sort' }, ...options) : null;
    sortSelect?.addEventListener('change', () => {
      const [i, d] = sortSelect.value.split(':');
      sortIndex = Number(i); sortDir = d;
      paintCards();
    });
    list = el('div', { class: 'card-list-rows' });
    return el('div', { class: 'card-list' },
      sortSelect ? el('div', { class: 'card-list-sort' }, el('span', null, 'Sort'), sortSelect) : null,
      list,
    );
  }
  function paintCards() {
    if (sortSelect && sortIndex >= 0) sortSelect.value = `${sortIndex}:${sortDir}`;
    const sorted = sortedRows();
    list.replaceChildren(...sorted.map(renderCard));
    onOrder?.({ rows: sorted, sortIndex, sortDir });
  }

  // ── Mount / mode switching ──────────────────────────────────────────────
  const wrapper = el('div', { class: 'table-scroll' });
  let mode = null;
  function mount() {
    const want = renderCard && MOBILE?.matches ? 'cards' : 'table';
    if (want !== mode) {
      mode = want;
      wrapper.replaceChildren(mode === 'cards' ? buildCards() : buildTable());
    }
    if (mode === 'cards') paintCards(); else paintTable();
  }
  mount();
  if (renderCard && MOBILE) {
    const onChange = () => {
      if (!wrapper.isConnected) { MOBILE.removeEventListener('change', onChange); return; }
      mount();
    };
    MOBILE.addEventListener('change', onChange);
  }
  return wrapper;
}

/**
 * One phone-sized row for a `.card-list`: a title, a muted meta line (strings
 * or nodes; empties skipped) and an optional right-hand column for a badge /
 * count. `onClick` makes the whole card the tap target and marks it as a row
 * link, which is what tells click-to-copy to stand down on touch devices.
 */
export function listCard({ title, meta = [], aside = [], onClick = null, class: cls = '' }) {
  const metaNodes  = meta.filter((m) => m != null && m !== '').map((m) => el('span', null, m));
  const asideNodes = aside.filter(Boolean);
  const attrs = { class: `list-card${cls ? ` ${cls}` : ''}` };
  if (onClick) {
    attrs.onClick = onClick;
    attrs['data-row-link'] = true;
    attrs.role = 'link';
    attrs.tabindex = '0';
    attrs.onKeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); onClick(e); } };
  }
  return el('div', attrs,
    el('div', { class: 'list-card-main' },
      el('div', { class: 'list-card-title' }, title ?? '—'),
      metaNodes.length ? el('div', { class: 'list-card-meta' }, ...metaNodes) : null,
    ),
    asideNodes.length ? el('div', { class: 'list-card-aside' }, ...asideNodes) : null,
  );
}

/**
 * Compare two sort keys. Nulls last. Numbers numerically, Timestamps/Dates
 * by epoch ms, everything else by locale-aware string compare.
 */
function cmp(a, b) {
  const an = a == null || a === '';
  const bn = b == null || b === '';
  if (an && bn) return 0;
  if (an) return 1;   // nulls always last
  if (bn) return -1;
  const am = toMillisIfTime(a);
  const bm = toMillisIfTime(b);
  if (am != null && bm != null) return am - bm;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function toMillisIfTime(v) {
  if (v && typeof v.toMillis === 'function') return v.toMillis(); // Firestore Timestamp
  if (v instanceof Date) return v.getTime();
  return null;
}
